/**
 * Custom Hook für Booking API-Operationen
 * Vereinfacht die Integration der Backend-API in React-Komponenten
 * Verwaltet Loading-States, Fehlerbehandlung und Caching
 */

import { useState, useEffect } from 'react';
import { fetchAvailability, submitBookingRequest, convertFormDataToBackendFormat, clearAvailabilityCache, getMonthRange, ValidationError } from '@/utils/api';

/**
 * Hook für Verfügbarkeitsdaten
 * Lädt automatisch die Verfügbarkeitsdaten für einen bestimmten Zeitraum
 * @param {Date} currentMonth - Aktueller Monat für den Kalender
 * @returns {Object} { data, loading, error, refetch, fetchForMonth }
 */
export function useAvailability(currentMonth = new Date()) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedRange, setLastFetchedRange] = useState(null);

  const fetchData = async (month = currentMonth) => {
    try {
      setLoading(true);
      setError(null);
      
      // Berechne Zeitraum für den Monat (startet immer heute)
      const { fromDate, toDate } = getMonthRange(month);
      
      
      const availability = await fetchAvailability(month);
      setData(availability);
      setLastFetchedRange({ fromDate, toDate, month: new Date(month) });
    } catch (err) {
      setError(err.message);
      console.error('Fehler beim Laden der Verfügbarkeitsdaten:', err);
    } finally {
      setLoading(false);
    }
  };

  // Lade Daten wenn sich der Monat ändert
  useEffect(() => {
    const { fromDate, toDate } = getMonthRange(currentMonth);
    
    // Prüfe ob wir bereits Daten für diesen Zeitraum haben
    if (!lastFetchedRange || 
        lastFetchedRange.fromDate !== fromDate || 
        lastFetchedRange.toDate !== toDate) {
      fetchData(currentMonth);
    }
  }, [currentMonth]);

  // Initial load
  useEffect(() => {
    if (!lastFetchedRange) {
      fetchData();
    }
  }, []);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(currentMonth),
    fetchForMonth: fetchData,
    lastFetchedRange
  };
}

/**
 * Mappt Backend-Feldnamen auf Frontend-Feldnamen
 * Backend verwendet verschachtelte Struktur (customer.first_name), Frontend flache Struktur (customer_name)
 * @param {string} backendField - Backend-Feldname (z.B. "customer.first_name")
 * @returns {string} Frontend-Feldname (z.B. "customer_name")
 */
function mapBackendFieldToFrontend(backendField) {
  const fieldMapping = {
    'customer.first_name': 'customer_name', // Wird aus customer_name aufgeteilt
    'customer.last_name': 'customer_name', // Wird aus customer_name aufgeteilt
    'customer.email': 'customer_email',
    'customer.phone': 'customer_phone',
    'customer.organization': 'customer_organization',
    'customer.tax_id': 'customer_tax_id',
    'customer.address.street': 'customer_street',
    'customer.address.zip_code': 'customer_zip',
    'customer.address.city': 'customer_city',
    'booking_details.event_purpose': 'event_purpose',
    'booking_details.start_datetime': 'start_time', // Kombiniert mit Datum
    'booking_details.end_datetime': 'end_time', // Kombiniert mit Datum
    'booking_details.notes': 'notes'
  };
  
  return fieldMapping[backendField] || backendField;
}

/**
 * Formatiert Fehlermeldungen für customer_name
 * Da das Backend separate Fehler für first_name und last_name sendet,
 * aber das Frontend nur ein Feld hat, müssen wir die Meldungen kombinieren
 * @param {Object} backendErrors - Backend-Fehler
 * @returns {string} Formatierte Fehlermeldung
 */
function formatNameErrors(backendErrors) {
  const firstNameErrors = backendErrors['customer.first_name'] || [];
  const lastNameErrors = backendErrors['customer.last_name'] || [];
  
  const allErrors = [...firstNameErrors, ...lastNameErrors];
  
  if (allErrors.length === 0) {
    return null;
  }
  
  // Wenn beide Felder Fehler haben, kombiniere sie
  if (firstNameErrors.length > 0 && lastNameErrors.length > 0) {
    return `Name: ${firstNameErrors[0]} ${lastNameErrors[0]}`;
  }
  
  // Wenn nur ein Feld einen Fehler hat, verwende dessen Meldung
  return allErrors[0];
}

/**
 * Konvertiert Backend-Validierungsfehler in Frontend-Format
 * Mappt verschachtelte Feldnamen auf flache Feldnamen
 * @param {Object} backendErrors - Backend-Fehler im Format { "customer.first_name": ["..."] }
 * @returns {Object} Frontend-Fehler im Format { "customer_name": "..." }
 */
function convertBackendErrorsToFrontend(backendErrors) {
  const frontendErrors = {};
  
  // Spezielle Behandlung für Name-Felder zuerst
  const nameError = formatNameErrors(backendErrors);
  if (nameError) {
    frontendErrors.customer_name = nameError;
  }
  
  // Verarbeite alle anderen Felder
  Object.keys(backendErrors).forEach(backendField => {
    // Überspringe first_name und last_name, da diese bereits behandelt wurden
    if (backendField === 'customer.first_name' || backendField === 'customer.last_name') {
      return;
    }
    
    const frontendField = mapBackendFieldToFrontend(backendField);
    const errorMessages = backendErrors[backendField];
    
    // Wenn mehrere Fehlermeldungen vorhanden sind, nimm die erste
    const errorMessage = Array.isArray(errorMessages) ? errorMessages[0] : errorMessages;
    
    // Wenn bereits ein Fehler für dieses Feld existiert, kombiniere die Meldungen
    if (frontendErrors[frontendField]) {
      frontendErrors[frontendField] += ' ' + errorMessage;
    } else {
      frontendErrors[frontendField] = errorMessage;
    }
  });
  
  return frontendErrors;
}

/**
 * Hook für Buchungsanfragen
 * Vereinfacht das Senden von Buchungsanfragen
 * @returns {Object} { submitBooking, loading, error, success, fieldErrors }
 */
export function useBookingSubmission() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const submitBooking = async (formData, selectedDateRange, totalPrice) => {
    try {
      setLoading(true);
      setError(null);
      setFieldErrors({});
      setSuccess(false);

      // Konvertiere Frontend-Daten in neues Backend-Format
      // totalPrice wird nicht mehr an die API gesendet, nur noch für Frontend-Anzeige verwendet
      const backendData = convertFormDataToBackendFormat(
        formData, 
        selectedDateRange, 
        totalPrice
      );

      // Sende Buchungsanfrage
      const result = await submitBookingRequest(backendData);
      
      setSuccess(true);
      return {
        success: true,
        bookingResponse: result, // Vollständige API-Response
        originalFormData: formData // Originale Formulardaten für Anzeige
      };

    } catch (err) {
      // Prüfe ob es ein ValidationError mit detaillierten Feld-Fehlern ist
      if (err instanceof ValidationError && err.errors) {
        const convertedErrors = convertBackendErrorsToFrontend(err.errors);
        setFieldErrors(convertedErrors);
        setError(err.message); // Generische Nachricht für allgemeine Anzeige
        console.error('Validierungsfehler beim Senden der Buchungsanfrage:', err);
        
        return {
          success: false,
          error: err.message,
          fieldErrors: convertedErrors
        };
      }
      
      // Für andere Fehler die generische Fehlermeldung verwenden
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      setFieldErrors({});
      console.error('Fehler beim Senden der Buchungsanfrage:', err);
      
      return {
        success: false,
        error: errorMessage,
        fieldErrors: {}
      };
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setError(null);
    setFieldErrors({});
    setSuccess(false);
  };

  return {
    submitBooking,
    loading,
    error,
    fieldErrors,
    success,
    resetState
  };
}

/**
 * Hilfsfunktion für detaillierte Fehlerbehandlung
 * Konvertiert Backend-Fehler in benutzerfreundliche Nachrichten
 * Angepasst für neue API-Validierungsregeln
 * @param {Error} error - Fehler-Objekt
 * @returns {string} Benutzerfreundliche Fehlermeldung
 */
function getErrorMessage(error) {
  const message = error.message.toLowerCase();
  
  // Neue API-spezifische Validierungsfehler
  if (message.includes('first_name') || message.includes('last_name')) {
    return "Ungültiger Name: Bitte verwenden Sie nur Buchstaben, Leerzeichen und Bindestriche.";
  }
  
  if (message.includes('email')) {
    return "Ungültige E-Mail-Adresse.";
  }
  
  if (message.includes('phone')) {
    return "Ungültige Telefonnummer: Bitte verwenden Sie ein gültiges internationales Format.";
  }
  
  if (message.includes('zip_code')) {
    return "Ungültige Postleitzahl: Bitte verwenden Sie eine 5-stellige PLZ.";
  }
  
  if (message.includes('street')) {
    return "Straßenadresse muss mindestens 5 Zeichen lang sein.";
  }
  
  if (message.includes('city')) {
    return "Stadtname muss mindestens 2 Zeichen lang sein.";
  }
  
  if (message.includes('start_datetime') || message.includes('end_datetime')) {
    return "Ungültiges Datum oder Zeitraum.";
  }
  
  if (message.includes('venue_id')) {
    return "Ungültige Veranstaltungsort-ID.";
  }
  
  if (message.includes('equipment_ids')) {
    return "Ungültige Ausstattungs-IDs.";
  }
  
  if (message.includes('validation failed') || message.includes('422')) {
    return "Validierungsfehler: Bitte überprüfen Sie Ihre Eingaben.";
  }
  
  if (message.includes('network')) {
    return "Netzwerkfehler: Bitte überprüfen Sie Ihre Internetverbindung.";
  }
  
  return "Fehler beim Senden der Buchungsanfrage. Bitte versuchen Sie es erneut.";
}

/**
 * Hook für Cache-Management
 * Ermöglicht das manuelle Leeren des Caches
 * @returns {Object} { clearCache }
 */
export function useApiCache() {
  const clearCache = (currentMonth = null) => {
    clearAvailabilityCache(currentMonth);
  };

  return { clearCache };
}
